// components/user/lead/calculator/UpgradeOptions.jsx
import React, { useEffect } from "react";
import { Card, Typography, Radio, Space, Divider } from "antd";
import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";

const { Text } = Typography;

const UpgradeOptions = ({
  upgradeOptions,
  selectedUpgrades,
  onToggleUpgrade,
  isDarkMode,
}) => {
  // Auto-select base options
  useEffect(() => {
    if (upgradeOptions?.length > 0 && selectedUpgrades.length === 0) {
      const baseSelections = [];
      upgradeOptions.forEach((option) => {
        const baseOption = option.options?.find((opt) => opt.is_base === true);
        if (baseOption) {
          baseSelections.push(baseOption.id);
        }
      });
      if (baseSelections.length > 0) {
        baseSelections.forEach((id) => onToggleUpgrade(id));
      }
    }
  }, [upgradeOptions]);

  const handleRadioChange = (optionId) => {
    onToggleUpgrade(optionId);
  };

  const getSelectedValue = (options) => {
    for (const opt of options) {
      if (selectedUpgrades.includes(opt.id)) {
        return opt.id;
      }
    }
    return null;
  };

  const cardBg = isDarkMode ? "bg-gray-800" : "bg-white";

  return (
    <Card
      className={`${cardBg} shadow-sm rounded-lg border-0`}
      bordered={false}
      size="small"
      styles={{
        body: { padding: "14px" },
      }}
    >
      {/* Compact Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-7 h-7 rounded bg-green-500">
          <ThunderboltOutlined className="text-white text-sm" />
        </div>
        <div>
          <Text
            strong
            className={`text-sm ${isDarkMode ? "text-white" : "text-gray-800"}`}
          >
            Device Upgrades
          </Text>
          <Text type="secondary" className="text-[10px] block -mt-0.5">
            Select upgraded configurations
          </Text>
        </div>
        <Text type="secondary" className="text-[10px] ml-auto text-right">
          One per category
        </Text>
      </div>

      {/* Upgrade Options */}
      <Space direction="vertical" size="small" className="w-full">
        {upgradeOptions.map((option, index) => {
          const selectedValue = getSelectedValue(option.options || []);

          return (
            <div key={option.id}>
              <Text
                className={`text-xs font-medium block mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {option.name}
              </Text>

              <Radio.Group
                value={selectedValue}
                onChange={(e) => handleRadioChange(e.target.value)}
                className="flex flex-wrap gap-1.5"
              >
                {option.options?.map((opt) => {
                  const isSelected = selectedValue === opt.id;

                  return (
                    <Radio.Button
                      key={opt.id}
                      value={opt.id}
                      className={`
                        !px-2.5 !py-1 !h-auto !text-xs !rounded
                        !transition-colors !duration-150
                        ${
                          isSelected
                            ? "!bg-green-500 !border-green-500 !text-white"
                            : isDarkMode
                              ? "!bg-gray-700 !border-gray-600 !text-gray-300 hover:!bg-gray-600"
                              : "!bg-gray-50 !border-gray-200 !text-gray-600 hover:!bg-gray-100"
                        }
                      `}
                    >
                      {opt.name}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>

              {index < upgradeOptions.length - 1 && (
                <Divider className="!my-2" />
              )}
            </div>
          );
        })}
      </Space>
    </Card>
  );
};

export default UpgradeOptions;
